import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Box, TextField, CloseIcon, Modal, Stack } from '@esgian/esgianui';
import TableMultiSelectFilter from '@components/Tables/TableMultiSelectFilter';
import TableMinMaxFilter from '@components/Tables/TableMinMaxFilter/TableMinMaxFilter';
import { DateRangePicker } from '@components/Inputs';
import { DATE_FORMAT } from '@constants';
import { isObject } from '@helpers';

const TableFiltersModal = ({ tableFilters, handleClose, handleSave, columns, data }) => {
  const [tempFilters, setTempFilters] = useState({});

  useEffect(() => {
    setTempFilters({ ...tableFilters });
  }, [tableFilters]);

  const handleFilterChange = (val) => {
    setTempFilters((prev) => ({ ...prev, ...val }));
  };
  const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

  const lookupData = useMemo(() => {
    let res = {};
    let filterableColumns = columns.filter(({ options }) => options.filter !== false);

    data.forEach((item) => {
      filterableColumns.forEach(({ name, options }) => {
        let value = getNestedValue(item, name);
        if (isObject(value)) {
          value = value['name'];
        }
        let inRes = res[name];

        if (options.customFilterType === 'multiselect') {
          if (!inRes) {
            res[name] = new Set();
          }
          res[name].add(value);
        }
      });
    });

    // Convert Sets to arrays before returning
    Object.keys(res).forEach((key) => {
      if (res[key] instanceof Set) {
        res[key] = Array.from(res[key]).sort((a, b) => String(a).localeCompare(String(b)));
      }
    });

    return res;
  }, [columns, data]);

  return (
    <Modal
      onClose={handleClose}
      open
      DialogContentProps={{ borderBottom: 'none' }}
      dividers={true}
      size={'md'}
      title={'Filters'}
      DialogActionsProps={{ sx: { justifyContent: 'center' } }}
      closeIcon={<CloseIcon fontSize={'small'} />}
      actions={[
        <Button
          sx={{ color: (theme) => theme.palette.text.primary }}
          key={'cancel-button'}
          variant={'text'}
          onClick={handleClose}>
          Cancel
        </Button>,
        <Button key={'save-button'} onClick={() => handleSave(tempFilters)}>
          Save
        </Button>
      ]}
      handleClose={(event, reason) => {
        if (reason === 'backdropClick') return; // Prevent closing when clicking outside
        handleClose();
      }}>
      <Box sx={{ p: 2, height: '60vh' }}>
        <Stack
          direction="row"
          spacing={2} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          {columns
            .filter(({ options }) => options.filter !== false)
            .map(({ options, label, name }) => {
              if (options.customFilterType === 'multiselect') {
                return (
                  <Stack
                    key={`${label}-filter`}
                    sx={{
                      flex: '0 1 calc(50% - 8px)', // No flex-grow, only flex-basis
                      maxWidth: 'calc(50% - 8px)' // Prevent growing beyond this
                    }}>
                    <Typography gutterBottom bold variant={'body2'}>
                      {label}
                    </Typography>
                    <TableMultiSelectFilter
                      onChange={(val) => {
                        handleFilterChange({ [name]: val });
                      }}
                      value={tempFilters[name]}
                      options={lookupData[name]}
                    />
                  </Stack>
                );
              }
              if (options.customFilterType === 'textField') {
                return (
                  <Stack
                    key={`${label}-filter`}
                    sx={{
                      flex: '0 1 calc(50% - 8px)', // No flex-grow, only flex-basis
                      maxWidth: 'calc(50% - 8px)' // Prevent growing beyond this
                    }}>
                    <Typography gutterBottom bold variant={'body2'}>
                      {label}
                    </Typography>
                    <TextField
                      size={'small'}
                      autoComplete={'off'}
                      value={tempFilters[name] ?? ''}
                      onChange={({ target }) => {
                        handleFilterChange({ [name]: target.value });
                      }}
                      sx={{ flex: '1 1 calc(50% - 4px)', minWidth: '200px' }}
                      variant="outlined"
                    />
                  </Stack>
                );
              }
              if (options.customFilterType === 'minMax') {
                return (
                  <Stack
                    key={`${label}-filter`}
                    sx={{
                      flex: '0 1 calc(50% - 8px)', // No flex-grow, only flex-basis
                      maxWidth: 'calc(50% - 8px)' // Prevent growing beyond this
                    }}>
                    <Typography gutterBottom bold variant={'body2'}>
                      {label}
                    </Typography>
                    <TableMinMaxFilter
                      value={tempFilters[name]}
                      onChange={(val) => {
                        handleFilterChange({ [name]: val });
                      }}
                    />
                  </Stack>
                );
              }
              if (options.customFilterType === 'dateBetween') {
                return (
                  <Stack
                    key={`${label}-filter`}
                    sx={{
                      flex: '0 1 calc(50% - 8px)', // No flex-grow, only flex-basis
                      maxWidth: 'calc(50% - 8px)' // Prevent growing beyond this
                    }}>
                    <Typography gutterBottom bold variant={'body2'}>
                      {label}
                    </Typography>
                    <DateRangePicker
                      disableFuture={false}
                      dateWheels={DATE_FORMAT}
                      dateFormat={DATE_FORMAT}
                      InputPropsSx={{ height: '39px' }}
                      inputSize={'small'}
                      startDate={tempFilters[name]?.startDate}
                      endDate={tempFilters[name]?.endDate}
                      onClose={(start, end) => {
                        let val = { startDate: start, endDate: end };
                        handleFilterChange({ [name]: val });
                      }}
                    />
                  </Stack>
                );
              }
            })}
        </Stack>
      </Box>
    </Modal>
  );
};

TableFiltersModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  tableFilters: PropTypes.object,
  handleSave: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object)
};

export default TableFiltersModal;
